/**
 * Direct Open edX client. There is no BFF — the browser talks to the LMS.
 *
 * Three things this file exists to get right, all of which are easy to get wrong
 * and produce confusing symptoms:
 *
 * 1. CREDENTIALS. Every request sends cookies (`credentials: 'include'`). Open edX
 *    authenticates browsers with a session cookie, so a request without it is simply
 *    anonymous — you get 401, not an error explaining why.
 *
 * 2. CSRF. Django rejects unsafe methods without a matching `X-CSRFToken` header.
 *    The token comes from the LMS's own endpoint, which returns it as JSON.
 *
 * 3. ORIGIN. Requests go to OUR origin, and the dev server (Caddy in production)
 *    proxies them to the LMS while FORWARDING the tenant Host. That is what makes
 *    Open edX resolve the right Site for site-scoped features like SSO providers.
 *    Pointing the app at the LMS's own hostname instead resolves the wrong tenant and
 *    fails in ways that look like configuration mistakes.
 */

/**
 * Empty by default, meaning SAME ORIGIN — the tenant's own hostname proxies the LMS.
 *
 * That is required, not preferred: Open edX resolves the current Site from the request
 * Host, so calling the LMS on its own hostname silently resolves the wrong tenant for
 * everything site-scoped (SSO providers, SiteConfiguration, theming, emails).
 */
const LMS = (import.meta.env.VITE_LMS_ORIGIN ?? '').replace(/\/$/, '');

export const lmsOrigin = LMS;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

/**
 * Forget the cached CSRF token, so the next unsafe request fetches a fresh one.
 *
 * Django ROTATES the CSRF token whenever `django.contrib.auth.login()` runs — it calls
 * `rotate_token()` unconditionally — and the browser silently replaces the `csrftoken`
 * cookie from that response. Anything holding the previous value is now wrong, and the
 * server answers the next write with
 * `403 {"detail":"CSRF Failed: CSRF token from the 'X-Csrftoken' HTTP header incorrect."}`.
 *
 * We log in a second time on purpose, once per editor: `startStudioSession()` mints the CMS
 * session the embedded XBlock editors need (see `unit.ts`), and that is a real `login()`.
 * Measured in a browser on k8s dev, 2026-08-29: cookie `j9bkbiDHg7…` before the mint,
 * `XI2bVVEAEG…` after it, and the first save that followed returned exactly that 403 —
 * matching the 82-byte 403s the component sweep recorded seven times.
 *
 * The 403 branch in `request()` recovers from this, which is why no author ever saw it. That
 * is a safety net, not the fix: it costs every first save a wasted round trip, and it makes a
 * genuine CSRF rejection indistinguishable from a stale cache in the log.
 */
export function invalidateCsrfToken() {
  csrfToken = null;
}

/** Fetch and cache the CSRF token. Open edX exposes it as JSON for exactly this. */
export async function getCsrfToken(force = false): Promise<string> {
  if (csrfToken && !force) return csrfToken;

  const res = await fetch(`${LMS}/csrf/api/v1/token`, { credentials: 'include' });
  if (!res.ok) throw new ApiError('Could not obtain a CSRF token', res.status);

  const data = (await res.json()) as { csrfToken?: string };
  if (!data.csrfToken) throw new ApiError('CSRF endpoint returned no token', res.status);

  csrfToken = data.csrfToken;
  return csrfToken;
}

interface Options {
  method?: string;
  json?: unknown;
  form?: Record<string, string>;
  /** Treat 401/403 as a value rather than an error — used for session probing. */
  allowUnauthenticated?: boolean;
  /**
   * Extra request headers. Merged over the defaults, but NOT over `X-CSRFToken` — that one
   * is computed here and a caller must not be able to replace it with a stale value.
   *
   * One caller needs this: the payment-operations screens send `X-Thinlms-Ui-Version`, which
   * the backend copies into `AdminPaymentAction.request_metadata.ui_version`. That field is
   * how the permanent audit row records that a refund was taken through the product rather
   * than by somebody driving the API by hand, and an empty one is indistinguishable from the
   * latter. It cannot be a body field: the backend reads it off the request headers for every
   * action, including the ones whose bodies it does not control.
   */
  headers?: Record<string, string>;
}

/* ----------------------------------------------------------- session refresh ---- */

let refreshInFlight: Promise<boolean> | null = null;
let onUnauthenticated: (() => void) | null = null;

/**
 * Register what to do when the session is genuinely gone.
 *
 * Called once, after a refresh attempt has already failed — so it means "signed out",
 * not "a request happened to 401".
 */
export function setUnauthenticatedHandler(handler: () => void) {
  onUnauthenticated = handler;
}

/**
 * Refresh the JWT cookie via Open edX's own endpoint.
 *
 * Open edX issues SHORT-LIVED JWT cookies alongside the session and expects the client
 * to refresh them; `frontend-platform` does this in an axios interceptor, and without an
 * equivalent every API call starts failing about an hour after sign-in while the user
 * still looks logged in.
 *
 * SINGLE-FLIGHT on purpose. A dashboard fires several requests at once, so N concurrent
 * 401s would otherwise trigger N refreshes — and Open edX rotates the token on each,
 * meaning the later ones invalidate the earlier and the user is logged out by the very
 * mechanism meant to keep them in. (mereka-lms had to patch frontend-platform for
 * exactly this.)
 */
async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${LMS}/login_refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-CSRFToken': await getCsrfToken() },
        });
        // The CSRF token rotates with the session, so drop the cached one either way.
        csrfToken = null;
        return res.ok;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * One request, with at most ONE retry.
 *
 * Two recoverable failures are handled, both invisible to callers:
 *   401 → the JWT expired. Refresh once, then retry.
 *   403 on an unsafe method → Django rotated the CSRF token. Re-fetch it, then retry.
 *
 * `attempt` caps the recursion at one retry. Without that cap a server that always 401s
 * produces an infinite refresh loop, which is worse than a visible error.
 */
export async function request<T>(path: string, options: Options = {}, attempt = 0): Promise<T> {
  const method = options.method ?? 'GET';
  // Caller headers first, so `Content-Type` and `X-CSRFToken` below always win. A caller
  // that could override the CSRF token could pin a stale one and turn every write into a
  // 403 that the retry path then "fixes" by fetching a fresh token it immediately discards.
  const headers: Record<string, string> = { ...(options.headers ?? {}), Accept: 'application/json' };
  let body: string | undefined;

  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.json);
  } else if (options.form) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
    body = new URLSearchParams(options.form).toString();
  }

  const unsafe = method !== 'GET' && method !== 'HEAD';
  if (unsafe) headers['X-CSRFToken'] = await getCsrfToken();

  const res = await fetch(`${LMS}${path}`, { method, headers, body, credentials: 'include' });

  if (res.ok) {
    return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
  }

  if (attempt === 0) {
    if (res.status === 401) {
      if (await refreshSession()) return request<T>(path, options, 1);
      // Refresh failed: the session is really gone. Tell the app once, then fall
      // through so the caller still gets a definite error.
      if (!options.allowUnauthenticated) onUnauthenticated?.();
    }
    if (res.status === 403 && unsafe) {
      await getCsrfToken(true);
      return request<T>(path, options, 1);
    }
  }

  if (options.allowUnauthenticated && (res.status === 401 || res.status === 403)) {
    return null as T;
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* proxies and login redirects return HTML */
  }
  throw new ApiError(`${method} ${path} failed`, res.status, payload);
}

export const openedx = {
  get: <T>(path: string, allowUnauthenticated = false) =>
    request<T>(path, { allowUnauthenticated }),
  post: <T>(path: string, json: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: 'POST', json, headers }),
  patch: <T>(path: string, json: unknown) => request<T>(path, { method: 'PATCH', json }),
  /** For endpoints where the body is the whole new state — course licences, so far. */
  put: <T>(path: string, json: unknown) => request<T>(path, { method: 'PUT', json }),
  postForm: <T>(path: string, form: Record<string, string>) =>
    request<T>(path, { method: 'POST', form }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/* ------------------------------------------------------------------ streaming ---- */

export interface StreamResult {
  /** Everything the body carried, whether or not `onChunk` ever fired. */
  text: string;
  /** Set when the server answered JSON instead of a stream; null for a real stream. */
  json: unknown | null;
}

/**
 * A POST whose body is read AS IT ARRIVES rather than parsed when it finishes.
 *
 * One endpoint needs this: the AI framework's `/openedx-ai-extensions/v1/workflows/`, which
 * answers `text/plain` and emits model output token by token. `request()` cannot serve it —
 * that function's contract is "resolve with parsed JSON", and JSON cannot be parsed until
 * the last byte, which is precisely the wait streaming exists to remove.
 *
 * IT LIVES HERE, not in `ai.ts`, so it inherits the three things this file exists to get
 * right: cookies, CSRF, and the single-flight JWT refresh. A caller that hand-rolled the
 * fetch would work perfectly until the first token expiry and then start failing at an hour
 * old — the exact bug the refresh above was written to prevent.
 *
 * SAME RETRY RULE AS `request`, WITH ONE NARROWING THAT MATTERS: a retry is only safe
 * BEFORE the first byte reaches `onChunk`. Both recoverable statuses (401, 403) are decided
 * on the response head, so that holds by construction — but a failure that surfaces mid-body
 * is thrown, never replayed, because replaying it would emit the opening of the answer twice.
 *
 * ANSWERS EITHER SHAPE ON PURPOSE. The same endpoint returns a stream for a profile whose
 * LLMProcessor has `stream: true` and a JSON object otherwise, and which one a workflow uses
 * is a database row, not a compile-time fact. Returning both rather than assuming one is
 * what keeps the caller correct when an operator flips that row.
 */
export async function requestStream(
  path: string,
  options: { json?: unknown; onChunk: (chunk: string) => void; signal?: AbortSignal },
  attempt = 0,
): Promise<StreamResult> {
  const headers: Record<string, string> = {
    Accept: 'text/plain, application/json',
    'X-CSRFToken': await getCsrfToken(),
  };
  let body: string | undefined;
  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.json);
  }

  const res = await fetch(`${LMS}${path}`, {
    method: 'POST',
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
  });

  if (!res.ok) {
    if (attempt === 0) {
      if (res.status === 401) {
        if (await refreshSession()) return requestStream(path, options, 1);
        onUnauthenticated?.();
      }
      if (res.status === 403) {
        await getCsrfToken(true);
        return requestStream(path, options, 1);
      }
    }
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      /* an error page or a proxy, not JSON */
    }
    throw new ApiError(`POST ${path} failed`, res.status, payload);
  }

  if ((res.headers.get('content-type') ?? '').includes('application/json')) {
    return { text: '', json: await res.json() };
  }

  if (!res.body) throw new ApiError(`POST ${path} returned no body to stream`, res.status);

  const reader = res.body.getReader();
  // `stream: true` on every decode, so a multi-byte character split across two network
  // chunks is held back rather than rendered as two replacement characters. Course content
  // is not ASCII — this is load-bearing for any academy that is not writing in English.
  const decoder = new TextDecoder();
  let text = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    if (!chunk) continue;
    text += chunk;
    options.onChunk(chunk);
  }

  return { text, json: null };
}

/**
 * Absolute URL for a unit's rendered content.
 *
 * Open edX renders every problem type, video, LTI tool and third-party XBlock as
 * server-side HTML. The app owns the chrome around it and embeds this.
 */
export function xblockEmbedUrl(usageKey: string): string {
  return `${LMS}/xblock/${encodeURIComponent(usageKey)}`;
}
